import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Globe, TrendingUp, DollarSign, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { IndustryOverviewAPIData, IndustryStat } from "@/schema/brand-os/brand-intel";

type StatDef = {
  label: string;
  Icon: LucideIcon;
  extractData: (api: IndustryOverviewAPIData) => { value: string; description?: string; subLabel: string } | null;
};

function roundDecimals(val: string): string {
  return val.replace(/\d+\.\d+/g, (match) => String(Math.round(parseFloat(match))));
}

const statDefinitions: StatDef[] = [
  {
    label: "Global Market",
    Icon: Globe,
    extractData: (api) => {
      if (!api.global_market?.value) return null;
      return { value: roundDecimals(api.global_market.value.startsWith("USD ") ? "$" + api.global_market.value.slice(4) : api.global_market.value), subLabel: api.global_market.period };
    },
  },
  {
    label: "YOY Growth",
    Icon: TrendingUp,
    extractData: (api) => {
      if (!api.yoy_growth?.value) return null;
      const cagrMatch = api.yoy_growth.value.match(/([\d.]+%)\s*CAGR/i);
      const raw = cagrMatch ? cagrMatch[1] : api.yoy_growth.value;
      return { value: roundDecimals(raw), subLabel: api.yoy_growth.period };
    },
  },
  {
    label: "Category Digital Ad Spend",
    Icon: DollarSign,
    extractData: (api) => {
      if (!api.category_digital_ad_spend?.value) return null;
      const raw = api.category_digital_ad_spend.value;
      const pctMatch = raw.match(/^([\d.]+%)\s+(.+)/);
      if (pctMatch) {
        return { value: pctMatch[1], description: pctMatch[2], subLabel: api.category_digital_ad_spend.period };
      }
      return { value: roundDecimals(raw.startsWith("USD ") ? "$" + raw.slice(4) : raw), subLabel: api.category_digital_ad_spend.period };
    },
  },
  {
    label: "Avg. Content Velocity",
    Icon: Zap,
    extractData: (api) => {
      const trend = api.content_velocity_trend;
      if (!trend?.length) return null;
      const latest = trend[trend.length - 1];
      if (!latest.assets_per_month) return null;
      return { value: String(Math.round(latest.assets_per_month)), subLabel: latest.label || "assets/mo" };
    },
  },
];

export function IndustryStatCards() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchIndustryOverview());

  const stats = statDefinitions.map((d) => {
    const result = data ? d.extractData(data) : null;
    const stat = result ? { label: d.label, value: result.value, sub_label: result.subLabel } : { label: d.label, value: "", sub_label: "" };
    return { stat, def: d, description: result?.description };
  });

  return <IndustryCardContent status={status} stats={stats} />;
}

function IndustryCardContent({ status, stats }: { status: QueryStatus; stats: { stat: IndustryStat; def: StatDef; description?: string }[] }) {
  switch (status) {
    case "pending":
      return <StatCardsSkeleton />;
    case "error":
      return <LockedCard message={message} />;
    case "success":
      return <StatCardsGrid stats={stats} />;
  }
}

function StatCard({ stat, def, description }: { stat: IndustryStat; def: StatDef; description?: string }) {
  if (!stat.value) return <LockedCard message={message} />;

  const { Icon } = def;
  return (
    <Card>
      <CardContent className="px-5 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Icon className="size-4" strokeWidth={2.5} />
          <p className="text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground">{stat.label}</p>
        </div>
        <p className="text-xl font-semibold lg:text-3xl text-foreground">{stat.value}</p>
        {description ? <p className="text-sm font-medium text-foreground">{description}</p> : null}
        {stat.sub_label ? <CardDescription>{stat.sub_label}</CardDescription> : null}
      </CardContent>
    </Card>
  );
}

function StatCardsGrid({ stats }: { stats: { stat: IndustryStat; def: StatDef; description?: string }[] }) {
  return (
    <div className="grid w-full gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {stats.map(({ stat, def, description }) => (
        <StatCard key={stat.label} stat={stat} def={def} description={description} />
      ))}
    </div>
  );
}

function StatCardsSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <CardContent className="px-5 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Skeleton className="size-10 rounded-sm" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-8 w-28 mt-1" />
            <Skeleton className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
